import { Component, Vue } from 'vue-property-decorator';
import Question from '@/components/Question/Question';
import { QuestionDTO } from '@/models/QuestionDTO';
import QuestionService from '@/services/question.service';
import RenounceButton from '@/components/RenounceButton/RenounceButton';
import LocalStoreService from '@/store/local.store';
import ParticipantService from '@/services/participant.service';
import { ProInclusionDTO } from '@/models/ProInclusionDTO';
import ProInclusion from '@/components/ProInclusion/ProInclusion';
import { store } from '@/store/store';
import { ProQuestionDTO } from '@/models/ProQuestionDTO';
import { McsisRole } from '@/models/constants/McsisRole';
import { ConsultationTypes } from '@/models/VariousEnum';
import DocumentService from '@/services/documentService';
import InformationBlock from '@/components/InformationBlock/InformationBlock';
import { isSimpleConsentUsedFromSU } from '@/config/app.config';

@Component({
  components: {
    Question,
    RenounceButton,
    ProInclusion,
    InformationBlock,
  },
})
export default class Questionnary extends Vue {
  public questions: QuestionDTO[] = [];
  public partId!: number;
  public showNextButtonOnFirstLoad: boolean = false;
  public errorMessage: string = '';
  public error: boolean = false;
  public proInclusion: ProInclusionDTO = {} as ProInclusionDTO;
  public isAuthenticated: boolean = false;
  public followUpPrinted: boolean = false;
  public rolePharmacist: string = McsisRole[McsisRole.Pharmacist];

  get showValidateButton(): boolean {
    return this.questions.length > 0
      && (this.questions.some((x) => x.answer === '1'));
  }

  get showNextButton(): boolean {
    return this.questions.length > 0
      && (this.questions.every((x) => x.answer === '0'));
  }

  protected mounted() {
    this.partId = Number(this.$route.params.partId);

    this.isAuthenticated = store.state.isAuthenticated;
    if (store.state.isAuthenticated) {
      QuestionService.fetchProQuestions(this.partId, false)
      .then((response: ProQuestionDTO) => {
        this.questions = response.questions;
        this.proInclusion = response.proInclusion;

        if (this.proInclusion.choice === null) {
          // Initialize component with default data
          this.proInclusion.choice = 'fit';
          this.proInclusion.consultationType = ConsultationTypes.short;
        }
      });
    } else {
      QuestionService.fetchQuestions(false)
      .then((response) => {
        this.questions = response;
      });
    }
  }

  protected showQuestion(question: QuestionDTO): boolean {
    const previousQuestions = this.questions.slice(0, this.questions.findIndex((q) => q === question));
    this.showNextButtonOnFirstLoad = true;
    return previousQuestions.every((x) => x.answer === '0');
  }

  protected showProInclusion(): boolean {
     return this.questions.every((x) => x.answer === '0') && this.isAuthenticated;
  }

  protected onSubmit(): void {
    this.$isInRole(McsisRole.Participant).then((isInRole: boolean) => {
      if (isInRole) {
        this.postQuestions();
      } else {
        this.postProQuestions();
      }
    });
  }

  protected sendToQueue(): void {
    ParticipantService.sendToQueue(this.partId)
    .then(() => {
      // Set type of inclusion: 'Inclusion' / 'Exclusion' / 'Refusal'
      LocalStoreService.setItem(LocalStoreService.localItemKeys.inclusionType, 'Exclusion');
      this.$router.push({
        name: 'confirmation',
        params: {
          screening: this.$route.params.screening,
        },
      });
    });
  }

  protected async postQuestions(): Promise<void> {
    QuestionService.postQuestions(
      this.questions,
      this.partId,
      false,
      isSimpleConsentUsedFromSU(this.$route.params.screening),
      this.showValidateButton,
      !this.showValidateButton,
      store.state.inclusionType)
    .then(async () => {
      if (this.showValidateButton) {
        await DocumentService.preparePDF(this.partId, this.$i18n.locale, true, this.$route.params.screening);
        this.sendToQueue();
      } else {
        this.$router.push({
          name: 'medicalcontent',
          params: {
            screening: this.$route.params.screening,
            partId: String(this.partId),
          },
        });
      }
    });
  }

  protected postProQuestions(): void {

    if (this.proInclusion.choice === 'colo') {
      this.proInclusion.height = Number(this.proInclusion.height);
      this.proInclusion.weight = Number(this.proInclusion.weight);

    } else {
      this.proInclusion.height = null;
      this.proInclusion.weight = null;
    }

    if (this.proInclusion.choice !== 'fit') {
      this.proInclusion.testFitNumber = '';
    }

    if (this.proInclusion.choice !== 'advice') {
      this.proInclusion.adviceChoice = '';
    }

    if (this.proInclusion.choice === null || this.proInclusion.choice === undefined) {
      this.proInclusion.choice = 'fit';
    }

    this.proInclusion.printDateTime = new Date();

    QuestionService.postProQuestions(this.questions, this.partId, false, this.proInclusion)
    .then(() => {
      if (this.showValidateButton) {
        this.sendToQueue();
      } else {
        this.$router.push({
          name: 'medicalcontent',
          params: {
            screening: this.$route.params.screening,
            partId: String(this.partId),
          },
        });
      }
    });
  }
}
